import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2, Lock } from 'lucide-react';
import { money } from './DeskForms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-credit-cashout`;
const inp = 'w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm';
const cents = (gross, pct) => { const g = Math.round(gross * 100); const fee = Math.round((g * pct) / 100 + 1e-9); return [fee / 100, (g - fee) / 100]; };

const Locked = ({ st, busy, onSubscribe }) => (
  <div className="space-y-3" data-testid="credit-cashout-locked">
    <p className="text-sm text-slate-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-400" /> {st.blocked_reason}</p>
    {st.can_subscribe && (
      <button onClick={onSubscribe} disabled={busy} data-testid="credit-cashout-subscribe-btn"
        className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Subscribe to Tier 2 · ${st.tier2_price_usd}/month
      </button>
    )}
  </div>
);

const CashoutForm = ({ st, busy, onSubmit }) => {
  const [f, setF] = useState({ amount_usd: '', payout_details: '' });
  const amount = parseFloat(f.amount_usd);
  const [fee, net] = cents(amount || 0, st.fee_pct);
  return (
    <div className="space-y-3" data-testid="credit-cashout-form">
      <p className="text-xs text-slate-400">Available credit: <span className="text-white font-semibold" data-testid="credit-cashout-available">{money(st.available_credit_usd)}</span></p>
      <input type="number" min="1" step="0.01" placeholder="Amount to draw (USD)" value={f.amount_usd} onChange={(e) => setF({ ...f, amount_usd: e.target.value })} className={inp} data-testid="credit-cashout-amount" />
      <textarea rows={2} placeholder="Where to pay you (bank details or a USDC wallet address)" value={f.payout_details} onChange={(e) => setF({ ...f, payout_details: e.target.value })} className={inp} data-testid="credit-cashout-payout" />
      <div className="text-xs text-slate-400 bg-slate-800/60 rounded-lg p-3 space-y-1" data-testid="credit-cashout-breakdown">
        <p className="flex justify-between"><span>Desk fee ({st.fee_pct}%)</span><span>{money(fee)}</span></p>
        <p className="flex justify-between text-white font-semibold"><span>You are paid (USD)</span><span>{money(net)}</span></p>
        <p className="text-slate-500">The full amount is drawn from your credit line under your signed Promissory Note.</p>
      </div>
      <button disabled={busy || !(amount >= 1) || amount > st.available_credit_usd || f.payout_details.trim().length < 3} data-testid="credit-cashout-submit"
        onClick={() => onSubmit({ amount_usd: amount, payout_details: f.payout_details }, () => setF({ amount_usd: '', payout_details: '' }))}
        className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Cash out from credit
      </button>
      {st.tier2_subscription?.status === 'active' && <p className="text-xs text-slate-500" data-testid="credit-cashout-tier2-active">Tier 2 active{st.tier2_subscription.cancel_at_period_end ? ' · ends this period' : ''}</p>}
    </div>
  );
};

export const CreditCashoutCard = ({ token, onRequested }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    axios.get(`${API}/status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setSt(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('cashout_session');
    if (!sid) { load(); return; }
    axios.post(`${API}/subscribe/confirm?session_id=${sid}`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => toast.success('Tier 2 active. You can now cash out from your credit line'))
      .catch(e => toast.error(e.response?.data?.detail?.toString() || 'Could not confirm Tier 2'))
      .finally(() => { window.history.replaceState({}, '', '/pas/desk'); load(); });
  }, [token, load]);

  const go = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Request failed'); }
    setBusy(false);
  };
  const subscribe = () => go(async () => { const r = await axios.post(`${API}/subscribe`, {}, auth); window.location.href = r.data.checkout_url; });
  const submit = (body, reset) => go(async () => { const r = await axios.post(`${API}/request`, body, auth); toast.success(r.data.message); reset(); load(); onRequested(); });

  if (!st || (!st.feature_enabled && !st.has_access)) return null;
  return (
    <div className="bg-slate-900 rounded-2xl p-5 border border-amber-500/30" data-testid="credit-cashout-card">
      <h2 className="font-semibold flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-amber-400" /> Cash out from your credit line <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">Tier 2</span></h2>
      <p className="text-xs text-slate-400 mb-4">Receive dollars straight from unused credit, no BETH needed. The admin approves each payout.</p>
      {st.has_access ? <CashoutForm st={st} busy={busy} onSubmit={submit} /> : <Locked st={st} busy={busy} onSubscribe={subscribe} />}
    </div>
  );
};
